
import React, { useEffect } from 'react';
import { CampingSite } from '@/services/campingSite/types';
import { InfoWindowContent } from './InfoWindowContent';
import { InfoWindowEventHandler } from './InfoWindowEventHandler';

interface SiteInfoWindowProps {
  selectedSite: CampingSite | null;
  mapInitialized: boolean;
  googleMapRef: React.MutableRefObject<google.maps.Map | null>;
  infoWindow: React.MutableRefObject<google.maps.InfoWindow | null>;
  onSiteSelect: (siteId: string) => void;
  handleBookNow: (site: CampingSite) => void;
}

const SiteInfoWindow: React.FC<SiteInfoWindowProps> = ({
  selectedSite,
  mapInitialized,
  googleMapRef,
  infoWindow,
  onSiteSelect,
  handleBookNow
}) => {
  // Manage InfoWindow content updates when selected site changes
  useEffect(() => {
    if (!window.google?.maps || !googleMapRef.current || !mapInitialized) return;
    
    // Close any existing InfoWindow
    if (infoWindow.current) {
      infoWindow.current.close();
    }
    
    if (selectedSite) {
      console.log('Creating InfoWindow for site:', selectedSite.id, selectedSite.siteNumber);
      
      const position = {
        lat: selectedSite.latitude || 0,
        lng: selectedSite.longitude || 0
      };
      
      // Create InfoWindow content using the utility function
      const content = document.createElement('div');
      content.className = 'infowindow-content p-0 max-w-full';
      
      const contentRenderer = InfoWindowContent({ site: selectedSite });
      content.innerHTML = contentRenderer.createContentHTML();
      
      if (!infoWindow.current) {
        infoWindow.current = new google.maps.InfoWindow({
          pixelOffset: new google.maps.Size(0, -5),
          maxWidth: 380,
        });
        
        // Add event listener for InfoWindow close
        google.maps.event.addListener(infoWindow.current, 'closeclick', () => {
          onSiteSelect('');
        });
      } else {
        // Update maxWidth for existing InfoWindow
        infoWindow.current.setOptions({
          maxWidth: 380
        });
      }
      
      // Set content and open the InfoWindow
      infoWindow.current.setContent(content);
      infoWindow.current.setPosition(position);
      infoWindow.current.open(googleMapRef.current);
      
      // Handle event listeners using the utility function
      const eventHandler = InfoWindowEventHandler({
        site: selectedSite,
        onSiteSelect,
        infoWindow,
        handleBookNow
      });
      
      // Start the attachment process with a small delay
      setTimeout(() => eventHandler.attachEventListeners(), 100);
    }
  }, [selectedSite, mapInitialized, onSiteSelect, googleMapRef, infoWindow, handleBookNow]);

  return null; // This component doesn't render anything directly
};

export default SiteInfoWindow;
